import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { LivyMessage } from './LivyMessage';
import { LivyAvatar } from './LivyAvatar';
import { LivyCapabilities } from './LivyCapabilities';
import { LivyComposer } from './LivyComposer';
import { COLUMN, PILL, SCROLLER, STARTER_CARD } from './styles';
import { LivyTheme } from './LivyTheme';
export const DISCLAIMER = {
  account: 'Livy reads live account data and can be wrong. Double-check anything you act on.',
  knowledge: "Livy answers from Livable's knowledge base and can be wrong. It cannot see any account."
};
function LivyPanelBody(_ref) {
  let messages = _ref.messages,
    streaming = _ref.streaming,
    error = _ref.error,
    companyLabel = _ref.companyLabel,
    _ref$showScope = _ref.showScope,
    showScope = _ref$showScope === void 0 ? true : _ref$showScope,
    conversationId = _ref.conversationId,
    surface = _ref.surface,
    _ref$starters = _ref.starters,
    starters = _ref$starters === void 0 ? [] : _ref$starters,
    _ref$reach = _ref.reach,
    reach = _ref$reach === void 0 ? 'account' : _ref$reach,
    reports = _ref.reports,
    pageHelp = _ref.pageHelp,
    onPageHelp = _ref.onPageHelp,
    onSend = _ref.onSend,
    onStop = _ref.onStop,
    onRetry = _ref.onRetry;
  const endRef = useRef(null);
  const scrollerRef = useRef(null);
  const _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    atBottom = _useState2[0],
    setAtBottom = _useState2[1];
  useEffect(() => {
    var _endRef$current;
    // Only follow the stream when the reader is already at the bottom. Yanking the view down while
    // someone is reading an earlier answer is the rudest thing a chat can do.
    if (atBottom) (_endRef$current = endRef.current) === null || _endRef$current === void 0 || _endRef$current.scrollIntoView({
      block: 'end',
      behavior: 'smooth'
    });
  }, [messages, atBottom]);
  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
  };
  const empty = messages.length === 0;
  return /*#__PURE__*/React.createElement(Stack, {
    sx: {
      height: '100%',
      minHeight: 0
    }
  }, showScope && companyLabel && reach === 'account' && /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: {
        xs: '16px',
        sm: '24px'
      },
      py: '7px',
      borderBottom: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: _objectSpread(_objectSpread({}, COLUMN), {}, {
      height: empty ? '100%' : undefined
    })
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11.5,
      color: 'text.secondary',
      letterSpacing: '.04em'
    }
  }, "Scope", /*#__PURE__*/React.createElement(Box, {
    component: "span",
    sx: {
      color: 'text.primary',
      fontWeight: 600,
      ml: '7px'
    }
  }, companyLabel)))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      position: 'relative',
      flex: 1,
      minHeight: 0,
      display: 'flex'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: SCROLLER,
    ref: scrollerRef,
    onScroll: handleScroll
  }, /*#__PURE__*/React.createElement(Box, {
    sx: COLUMN,
    role: "log",
    "aria-label": "Conversation with Livy",
    "aria-busy": streaming
  }, empty &&
  /*#__PURE__*/
  // An opening screen anchored to the top of a tall empty panel looks like a page that
  // failed to load; centring it makes the emptiness deliberate.
  React.createElement(Stack, {
    alignItems: "center",
    justifyContent: "center",
    sx: {
      minHeight: '100%',
      py: '24px',
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      mb: '14px'
    }
  }, /*#__PURE__*/React.createElement(LivyAvatar, {
    size: 84
  })), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 22,
      fontWeight: 600,
      letterSpacing: '-.01em',
      mb: '4px'
    }
  }, "What can I look into?"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: pageHelp ? '10px' : '20px',
      fontSize: 14
    }
  }, "Ask about a property, a resident, a bill or a billing cycle."), pageHelp && onPageHelp && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: onPageHelp,
    sx: {
      textTransform: 'none',
      mb: '16px',
      fontSize: 13
    }
  }, "Help for this page: ", pageHelp), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'grid',
      gap: '10px',
      gridTemplateColumns: {
        xs: '1fr',
        sm: '1fr 1fr'
      },
      mb: '28px'
    }
  }, starters.map(s => /*#__PURE__*/React.createElement(Button, {
    key: s,
    disableRipple: true,
    onClick: () => onSend(s),
    sx: STARTER_CARD
  }, s))), reports && reports.length > 0 && /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: '100%',
      mb: '24px',
      textAlign: 'left'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11.5,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '8px'
    }
  }, "Popular reports"), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    sx: {
      flexWrap: 'wrap',
      gap: '8px'
    }
  }, reports.map(r => /*#__PURE__*/React.createElement(Button, {
    key: r,
    disableRipple: true,
    onClick: () => onSend(r),
    sx: PILL
  }, r)))), !reports && /*#__PURE__*/React.createElement(LivyCapabilities, {
    surface: surface !== null && surface !== void 0 ? surface : 'admin',
    onAsk: onSend
  })), messages.map((m, i) => /*#__PURE__*/React.createElement(LivyMessage, {
    key: i,
    message: m,
    startsExchange: m.role === 'user' && i > 0,
    conversationId: conversationId
    // Index is a stable id within a conversation: turns are only ever appended.
    ,
    turnId: "t".concat(i),
    surface: surface
    // Only the last message can be mid-stream, and only it should show a caret.
    ,
    streaming: streaming && i === messages.length - 1 && m.role === 'assistant',
    onRetry: !streaming && i === messages.length - 1 && m.role === 'assistant' ? onRetry : undefined,
    onSuggestion: onSend
  })), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    variant: "outlined",
    sx: {
      mt: '8px',
      borderRadius: '10px'
    },
    action: onRetry ? /*#__PURE__*/React.createElement(Button, {
      size: "small",
      color: "inherit",
      onClick: onRetry,
      sx: {
        textTransform: 'none'
      }
    }, "Try again") : undefined
  }, error), /*#__PURE__*/React.createElement("div", {
    ref: endRef
  }))), !atBottom && messages.length > 0 && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: () => {
      var _endRef$current2;
      return (_endRef$current2 = endRef.current) === null || _endRef$current2 === void 0 ? void 0 : _endRef$current2.scrollIntoView({
        block: 'end',
        behavior: 'smooth'
      });
    },
    sx: {
      position: 'absolute',
      bottom: 12,
      left: '50%',
      transform: 'translateX(-50%)',
      borderRadius: '14px',
      textTransform: 'none',
      fontSize: 12,
      px: '12px',
      py: '3px',
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      '&:hover': {
        bgcolor: 'action.hover'
      }
    }
  }, "\u2193 Latest")), /*#__PURE__*/React.createElement(LivyComposer, {
    onSend: onSend,
    onStop: onStop,
    streaming: streaming,
    placeholder: "Ask about a property, a resident, or a billing cycle\u2026",
    disclaimer: DISCLAIMER[reach]
  }));
}

/** Ripples off inside Livy: see LivyTheme. */
export function LivyPanel(props) {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyPanelBody, props));
}